// Shared UI building blocks for the entity detail views: the back-bar at
// the top of every page, the stats grid, the year chart, the rank rows,
// and the recent-event row. Keeping these in one place means the four
// detail views (Friend / Category / Trip / Tag) can stay short and look
// consistent.

import React, { useEffect, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import InsightsIcon from '@mui/icons-material/Insights';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { MyAvatar } from '../common/MyAvatar';

// ---- detail tabs ------------------------------------------------------
//
// A two-pill tab bar that drives a URL segment (so the active tab is
// shareable / back-button-aware). The parent owns navigation: it passes
// the active id and an onSelect(tabId) callback that should `navigateTo`
// the right detail-tab path.

export const DetailTabs = ({active, onSelect}) => (
  <div className="detail-view__tabs" role="tablist">
    <button
      type="button"
      role="tab"
      aria-selected={active === 'summary'}
      className={`detail-view__tab ${active === 'summary' ? 'is-active' : ''}`.trim()}
      onClick={() => onSelect('summary')}
    >
      <InsightsIcon/> <span>Summary</span>
    </button>
    <button
      type="button"
      role="tab"
      aria-selected={active === 'events'}
      className={`detail-view__tab ${active === 'events' ? 'is-active' : ''}`.trim()}
      onClick={() => onSelect('events')}
    >
      <EventNoteIcon/> <span>Events</span>
    </button>
  </div>
);

// ---- hero bar ---------------------------------------------------------
//
// Floats over the top of the hero gradient with everything the user
// might want to reach from the page header: back button (left), the
// entity name (middle, only visible when the hero is collapsed),
// Summary/Events tab toggle, and — on the user's own map — the New
// Event + Edit icon buttons. The bar always renders the title slot;
// CSS opacity-fades it based on the parent's --collapsed modifier so we
// can cross-fade with the big centered title in the hero body.

export const HeroBar = ({
  backLabel = 'Back',
  onBack,
  onNewEvent,
  onEdit,
  editLabel = 'Edit',
  showActions = true,
  entityTitle,
  tab,
  onSelectTab,
}) => (
  <div className="detail-view__hero-bar">
    <button
      type="button"
      className="detail-view__back-btn"
      onClick={onBack}
    >
      <ArrowBackIcon/> <span>{backLabel}</span>
    </button>
    <div className="detail-view__hero-bar-title" aria-hidden="true">
      {entityTitle || ''}
    </div>
    {tab && onSelectTab &&
      <DetailTabs active={tab} onSelect={onSelectTab}/>
    }
    {showActions && (onNewEvent || onEdit) &&
      <div className="detail-view__hero-bar-actions">
        {onNewEvent &&
          <button
            type="button"
            className="detail-view__hero-icon-btn detail-view__hero-icon-btn--accent"
            aria-label="New Event"
            title="New Event"
            onClick={onNewEvent}
          >
            <AddIcon/>
          </button>
        }
        {onEdit &&
          <button
            type="button"
            className="detail-view__hero-icon-btn"
            aria-label={editLabel}
            title={editLabel}
            onClick={onEdit}
          >
            <EditIcon/>
          </button>
        }
      </div>
    }
  </div>
);

// ---- stat tile ---------------------------------------------------------

const formatNumber = (n) => Number(n).toLocaleString('en-US');

const useCountUp = (target, duration = 1200, delay = 0) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if(!Number.isFinite(target) || target <= 0){
      setValue(target || 0);
      return undefined;
    }
    let raf;
    const timeoutId = setTimeout(() => {
      const start = performance.now();
      const step = (now) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(Math.round(target * eased));
        if(progress < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => {
      clearTimeout(timeoutId);
      if(raf) cancelAnimationFrame(raf);
    };
  }, [target, duration, delay]);
  return value;
};

export const StatTile = ({label, value, accent, delay, icon, isText}) => {
  // Animate numeric stats with a count-up; text stats render as-is.
  const numericValue = !isText && typeof value === 'number' ? value : null;
  const animated = useCountUp(numericValue ?? 0, 1200, delay);
  const display = isText
    ? value
    : (numericValue != null ? formatNumber(animated) : value);
  return (
    <div
      className="detail-view__stat"
      style={{'--stat-delay': `${delay}ms`, '--stat-accent': accent}}
    >
      {icon && <div className="detail-view__stat-icon" aria-hidden="true">{icon}</div>}
      <div className={`detail-view__stat-value ${isText ? 'detail-view__stat-value--text' : ''}`.trim()}>
        {display}
      </div>
      <div className="detail-view__stat-label">{label}</div>
    </div>
  );
};

// ---- year chart --------------------------------------------------------

export const YearChart = ({entries, accentTop = '#818cf8', accentBottom = '#6366f1'}) => {
  const maxCount = entries.reduce((max, [, count]) => Math.max(max, count), 0);
  if(maxCount === 0) return null;
  return (
    <div className="detail-view__year-chart" role="list">
      {entries.map(([year, count], idx) => {
        const heightPercent = Math.max(2, (count / maxCount) * 100);
        return (
          <div
            className="detail-view__year-bar"
            key={year}
            role="listitem"
            aria-label={`${year}: ${count} ${count === 1 ? 'event' : 'events'}`}
            style={{'--bar-delay': `${280 + idx * 55}ms`}}
          >
            <div className="detail-view__year-bar-track">
              <div
                className="detail-view__year-bar-fill"
                style={{
                  height: `${heightPercent}%`,
                  '--bar-color-top': accentTop,
                  '--bar-color-bottom': accentBottom,
                }}
              />
              <div className="detail-view__year-bar-count">{count}</div>
            </div>
            <div className="detail-view__year-bar-label">{year}</div>
          </div>
        );
      })}
    </div>
  );
};

// ---- rank rows ---------------------------------------------------------

export const CategoryRankRow = ({category, count, percent, delay, onClick}) => {
  const Component = onClick ? 'button' : 'div';
  const extraProps = onClick ? {type: 'button'} : {};
  return (
    <Component
      className={`detail-view__rank-row ${onClick ? 'detail-view__rank-row--clickable' : ''}`.trim()}
      style={{'--row-delay': `${delay}ms`, '--bar-target-width': `${percent}%`, '--bar-fill': category.pinColor || '#6366f1'}}
      onClick={onClick}
      {...extraProps}
    >
      <div className="detail-view__rank-icon" style={{backgroundColor: category.pinColor}}>
        <span aria-hidden="true">{category.defaultEmoji}</span>
      </div>
      <div className="detail-view__rank-body">
        <div className="detail-view__rank-label-row">
          <span className="detail-view__rank-name">{category.name}</span>
          <span className="detail-view__rank-count">{count}</span>
        </div>
        <div className="detail-view__rank-bar">
          <div
            className="detail-view__rank-bar-fill"
            style={{backgroundColor: category.pinColor}}
          />
        </div>
      </div>
    </Component>
  );
};

export const FriendRankRow = ({friend, count, percent, delay, onClick}) => {
  const Component = onClick ? 'button' : 'div';
  const extraProps = onClick ? {type: 'button'} : {};
  return (
    <Component
      className={`detail-view__rank-row ${onClick ? 'detail-view__rank-row--clickable' : ''}`.trim()}
      style={{'--row-delay': `${delay}ms`, '--bar-target-width': `${percent}%`, '--bar-fill': 'linear-gradient(90deg, #f59e0b, #ec4899)'}}
      onClick={onClick}
      {...extraProps}
    >
      <MyAvatar
        userAccount={friend}
        style={{height:36, width:36, fontSize:14, flexShrink:0}}
      />
      <div className="detail-view__rank-body">
        <div className="detail-view__rank-label-row">
          <span className="detail-view__rank-name">{friend.name}</span>
          <span className="detail-view__rank-count">{count}</span>
        </div>
        <div className="detail-view__rank-bar">
          <div
            className="detail-view__rank-bar-fill"
            style={{background: 'linear-gradient(90deg, #f59e0b, #ec4899)'}}
          />
        </div>
      </div>
    </Component>
  );
};

export const PlaceRankRow = ({label, count, percent, delay}) => (
  <div
    className="detail-view__rank-row"
    style={{'--row-delay': `${delay}ms`, '--bar-target-width': `${percent}%`}}
  >
    <div
      className="detail-view__rank-icon"
      style={{backgroundColor: '#0ea5e9'}}
    >
      <span aria-hidden="true">📍</span>
    </div>
    <div className="detail-view__rank-body">
      <div className="detail-view__rank-label-row">
        <span className="detail-view__rank-name">{label}</span>
        <span className="detail-view__rank-count">{count}</span>
      </div>
      <div className="detail-view__rank-bar">
        <div
          className="detail-view__rank-bar-fill"
          style={{background: 'linear-gradient(90deg, #0ea5e9, #6366f1)'}}
        />
      </div>
    </div>
  </div>
);

// ---- recent event row --------------------------------------------------

const SHORT_MONTH = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export const EventRow = ({event, startAt, category, onClick, delay}) => {
  return (
    <button
      type="button"
      className="detail-view__event-row"
      style={{'--row-delay': `${delay}ms`}}
      onClick={onClick}
    >
      <div className="detail-view__event-date" aria-hidden="true">
        <div className="detail-view__event-date-day">{startAt.getDate()}</div>
        <div className="detail-view__event-date-month">{SHORT_MONTH[startAt.getMonth()]}</div>
        <div className="detail-view__event-date-year">{startAt.getFullYear()}</div>
      </div>
      <div className="detail-view__event-text">
        <div className="detail-view__event-title">{event.title || 'Untitled event'}</div>
        <div className="detail-view__event-meta">
          {event.location?.description || (category ? category.name : '')}
        </div>
      </div>
      <div className="detail-view__event-cat" style={{backgroundColor: category?.pinColor || '#6366f1'}}>
        {category?.defaultEmoji || <ChevronRightIcon style={{color:'#ffffff', fontSize:16}}/>}
      </div>
    </button>
  );
};
